import { z } from 'zod';
import {
  EstimateSchema,
  isEstimateEditable,
  nextEstimateVersion,
  sortEstimates,
  type Estimate,
} from '../../domain/entities/estimate.ts';
import { CostLineSchema, type CostLine } from '../../domain/entities/cost-line.ts';
import { newId } from '../../domain/id.ts';
import { expectUnchanged } from '../../ports/batch.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import type { Unsubscribe } from '../../ports/query.ts';
import { NotFoundError } from '../../ports/repository.ts';

/**
 * Writing estimates, and nothing about approving one.
 *
 * Approval lives in a callable (`approveEstimate`), because it writes
 * budget rows in another collection and supersedes a previous version — a
 * client that could do that could approve its own estimate. Everything
 * here is the part a draft's own author does.
 */
export class EstimateError extends Error {
  readonly reason: 'invalid' | 'notEditable';

  constructor(reason: 'invalid' | 'notEditable', message: string) {
    super(message);
    this.name = 'EstimateError';
    this.reason = reason;
  }
}

export const SaveEstimateInputSchema = z.object({
  title: z.string().trim().min(1, 'An estimate needs a title').max(200),
  description: z.string().max(20_000).optional(),
  lines: z.array(CostLineSchema).optional(),
  tagIds: z.array(z.string().min(1)).optional(),
});
export type SaveEstimateInput = z.input<typeof SaveEstimateInputSchema>;

export interface EstimateContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly jobId: string;
  readonly uid: string;
}

type ReadContext = Pick<EstimateContext, 'data' | 'companyId' | 'jobId'>;

function repo(context: ReadContext) {
  return context.data.collection<Estimate>(
    { companyId: context.companyId, jobId: context.jobId },
    'estimates',
  );
}

/**
 * A job's estimates, newest version first.
 *
 * Ordered by `version` descending in the query rather than sorted after,
 * so the one anybody wants is the first page's first row. `sortEstimates`
 * runs again in `subscribeToEstimates` as a tiebreak, which costs nothing
 * and means a caller holding an unordered list still renders right.
 */
export async function listEstimates(context: ReadContext): Promise<Estimate[]> {
  const page = await repo(context).list({
    orderBy: [{ field: 'version', direction: 'desc' }],
  });
  return [...page.items];
}

export function subscribeToEstimates(
  context: ReadContext,
  onChange: (estimates: readonly Estimate[]) => void,
  onError: (cause: Error) => void,
): Unsubscribe {
  return repo(context).subscribe(
    { orderBy: [{ field: 'version', direction: 'desc' }] },
    (page) => onChange(sortEstimates(page.items)),
    onError,
  );
}

/**
 * Start a new estimate.
 *
 * The version is the highest existing plus one, read here rather than
 * allocated by a counter — and that is a smaller promise than the change
 * order numbering makes, deliberately. Two people starting an estimate at
 * the same moment would both get version 4, which is untidy and harmless:
 * a version is a label on a document, not an identifier anything points
 * at, and renaming one costs nothing. A change order number is read out
 * over the telephone and written on an invoice, which is why that one is
 * worth a transaction and this one is not. P5-05b records the trade.
 */
export async function createEstimate(
  context: EstimateContext,
  input: SaveEstimateInput,
): Promise<Estimate> {
  const parsed = SaveEstimateInputSchema.safeParse(input);
  if (!parsed.success) throw new EstimateError('invalid', parsed.error.issues[0]!.message);

  const existing = await listEstimates(context);
  const now = context.clock.now().toISOString();

  const checked = EstimateSchema.safeParse({
    id: newId(),
    companyId: context.companyId,
    jobId: context.jobId,
    version: nextEstimateVersion(existing),
    title: parsed.data.title,
    description: parsed.data.description ?? '',
    status: 'draft',
    lines: parsed.data.lines ?? [],
    tagIds: parsed.data.tagIds ?? [],
    createdAt: now,
    createdBy: context.uid,
    updatedAt: now,
    updatedBy: context.uid,
  });
  if (!checked.success) throw new EstimateError('invalid', checked.error.issues[0]!.message);

  await repo(context).create(checked.data);
  return checked.data;
}

/**
 * Start a new version from an existing one.
 *
 * Copies the lines with **new ids**, which is the one thing about this
 * worth reading twice. Keeping the ids would make `diffEstimates` report
 * version 4 as identical to version 3 even after every price moved — it
 * matches by id, and two documents sharing line ids are two documents the
 * diff cannot tell apart. New ids mean the diff compares *this* version's
 * lines against the one it came from, which is what somebody asking "what
 * changed" means.
 */
export async function reviseEstimate(
  context: EstimateContext,
  fromId: string,
  makeId: () => string = newId,
): Promise<Estimate> {
  const source = await repo(context).get(fromId);
  if (!source) throw new NotFoundError('estimates', fromId);

  return createEstimate(context, {
    title: source.title,
    description: source.description,
    lines: source.lines.map((line) => ({ ...line, id: makeId() })),
    tagIds: [...source.tagIds],
  });
}

export async function saveEstimate(
  context: EstimateContext,
  id: string,
  input: SaveEstimateInput,
): Promise<Estimate> {
  const parsed = SaveEstimateInputSchema.safeParse(input);
  if (!parsed.success) throw new EstimateError('invalid', parsed.error.issues[0]!.message);

  const existing = await repo(context).get(id);
  if (!existing) throw new NotFoundError('estimates', id);
  if (!isEstimateEditable(existing.status)) {
    throw new EstimateError(
      'notEditable',
      `A ${existing.status} estimate cannot be changed. Start a new version instead.`,
    );
  }

  const patch = {
    title: parsed.data.title,
    description: parsed.data.description ?? existing.description,
    lines: parsed.data.lines ?? existing.lines,
    tagIds: parsed.data.tagIds ?? existing.tagIds,
    updatedAt: context.clock.now().toISOString(),
    updatedBy: context.uid,
  };

  const checked = EstimateSchema.safeParse({ ...existing, ...patch });
  if (!checked.success) throw new EstimateError('invalid', checked.error.issues[0]!.message);

  await context.data.commit([
    {
      kind: 'update',
      scope: { companyId: context.companyId, jobId: context.jobId },
      collection: 'estimates' as const,
      id,
      patch,
      // The version it read. Two people editing one draft is the realistic
      // case here — an estimator and whoever is checking their numbers —
      // and a silent last-write-wins would lose a morning's pricing.
      expect: expectUnchanged(existing),
    },
  ]);
  return checked.data;
}

/**
 * Void a draft.
 *
 * Voided rather than deleted, like everything else in this phase: the
 * version number it used stays used, so two different documents are never
 * both "version 3" in somebody's email.
 */
export async function voidEstimate(
  context: EstimateContext,
  id: string,
  reason: string,
): Promise<Estimate> {
  const existing = await repo(context).get(id);
  if (!existing) throw new NotFoundError('estimates', id);
  if (existing.status !== 'draft') {
    throw new EstimateError('notEditable', `A ${existing.status} estimate cannot be voided here`);
  }

  const patch = {
    status: 'void' as const,
    reason,
    updatedAt: context.clock.now().toISOString(),
    updatedBy: context.uid,
  };
  await context.data.commit([
    {
      kind: 'update',
      scope: { companyId: context.companyId, jobId: context.jobId },
      collection: 'estimates' as const,
      id,
      patch,
      expect: expectUnchanged(existing),
    },
  ]);
  return { ...existing, ...patch };
}

/**
 * Replace one line in a list, for a form editing a row at a time.
 *
 * `withLineReplaced` rather than `replaceLine`, because change orders
 * already have a `removeLine` that takes a context and writes. These two
 * are pure array helpers and the names say which kind they are.
 */
export function withLineReplaced(lines: readonly CostLine[], line: CostLine): readonly CostLine[] {
  const at = lines.findIndex((existing) => existing.id === line.id);
  if (at < 0) return [...lines, line];
  return lines.map((existing) => (existing.id === line.id ? line : existing));
}

/** Remove one line. Pure, unlike the change order function of a similar name. */
export function withLineRemoved(lines: readonly CostLine[], id: string): readonly CostLine[] {
  return lines.filter((line) => line.id !== id);
}
